import Modal from 'react-modal';
import React, { useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import LoadingSpinner from '../../common/LoadingSpinner';
import { getCSRFToken, ROOT_URL_V2 } from '../../../config';
import { getErrorMessages, getMatchingModeLabel } from '../../../utils';
import { TMatchingMode, TPageNotification, TSeries } from '../../../types';
import { ErrorMessage } from '../../common';
import { matchingModeCodes, matchingModeLabels, TMatchingModeKeys } from '../../../consts';


type TModalConfirmChangeMatchingModeProps = {
    isOpen: boolean;
    isLoading: boolean;
    handleClose: () => void;
    handleConfirm: () => void,
    seriesId: number;
    matchingMode: TMatchingMode;
}

export function ModalConfirmChangeMatchingMode({
    isOpen,
    isLoading,
    handleClose,
    handleConfirm,
    seriesId,
    matchingMode,
}: TModalConfirmChangeMatchingModeProps) {

    const modeLabel = getMatchingModeLabel(matchingMode)?.toLowerCase();

    return (
        <Modal
            className="change-mode-modal"
            isOpen={isOpen}
            onRequestClose={handleClose}
            contentLabel="Confirm matching change"
            ariaHideApp={false}
        >
            <p className="modal-title">Confirm matching change</p>
            <div className="change-mode-body">
                <p>
                    Are you sure you want to change the matching for the ongoing job <b>{seriesId}</b> to <b>{modeLabel}</b>?
                </p>
            </div>
            <button
                className="continue btn--round-big"
                onClick={handleConfirm}
                type="button"
                disabled={isLoading}
            >
                {isLoading ? (
                    <LoadingSpinner
                        className="change-mode-spinner"
                        backgroundColor="white"
                    />
                ) : (
                    'CONFIRM'
                )}
            </button>
            {!isLoading && (
                <button className="back" onClick={handleClose} type="button">
                    CANCEL
                </button>
            )}
        </Modal>
    );
}


type TChangeMatchingModeProps = {
    notification: TPageNotification;
    reloadData: () => void;
    handleClose: (modalName: string) => void;
    isOpen: boolean;
    series: TSeries;
}

export function ModalChangeMatchingMode({
    notification,
    reloadData,
    handleClose,
    isOpen,
    series,
}: TChangeMatchingModeProps) {
    const [selectedMode, setSelectedMode] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isModalConfirmChangeMatchingModeOpen, setIsModalConfirmChangeMatchingModeOpen] = useState(false);

    const matchingModes =
        Object.keys(matchingModeCodes).reduce((acc, key: TMatchingModeKeys) => {
            if (matchingModeCodes[key] !== series.matching_mode && matchingModeLabels[key]) {
                // @ts-ignore
                acc[matchingModeCodes[key]] = matchingModeLabels[key];
            }
            return acc;
        }, {});

    const matchingModeOptions =
        Object.entries(matchingModes).map(([key, value]) => ({
            label: value,
            value: key,
        }));

    const handleOpenModalConfirmChangeMatchingMode = () => {
        setIsModalConfirmChangeMatchingModeOpen(true);
    };

    const handleCloseModalConfirmChangeMatchingMode = () => {
        setIsModalConfirmChangeMatchingModeOpen(false);
    };

    const handleContinue = (): void => {
        setError(null);
        handleOpenModalConfirmChangeMatchingMode();
    };

    const handleConfirm = (): void => {
        setIsLoading(true);

        axios({
            method: 'patch',
            url: `${ROOT_URL_V2}series/${series.id}/update_series/`,
            data: {
                jobs_only: true,
                matching_mode: selectedMode,
            },
            headers: {
                'X-CSRFToken': getCSRFToken(),
            },
        }).then(() => {
            setIsLoading(false);
            handleClose('ChangeMatchingMode');
            notification.success(
                'Success',
                `${series.id} successfully edited`
            );
            reloadData();
        }).catch(err => {
            setIsLoading(false);
            setError(getErrorMessages(err));
            handleCloseModalConfirmChangeMatchingMode();
        });
    };


    return (
        <Modal
            className="change-mode-modal"
            isOpen={isOpen}
            onRequestClose={() => handleClose('ChangeMatchingMode')}
            contentLabel="Change matching mode"
            ariaHideApp={false}
        >
            <p className="modal-title">Change matching</p>
            <div className="change-mode-body">
                <div>
                    <p className="change-mode-text">
                        Select a new type for matching workers for the ongoing job <b>{series.id}</b>:
                    </p>
                    <Select
                        name="select-status"
                        value={selectedMode}
                        clearable={false}
                        onChange={e => setSelectedMode(e.value)}
                        options={matchingModeOptions}
                    />
                </div>
            </div>
            <button
                className="continue btn--round-big"
                onClick={handleContinue}
                type="button"
                disabled={!selectedMode}
            >
                CONTINUE
            </button>
            <button className="back" onClick={() => handleClose('ChangeMatchingMode')} type="button">
                CANCEL
            </button>

            {!!error && <ErrorMessage message={error} />}

            {isModalConfirmChangeMatchingModeOpen && (
                <ModalConfirmChangeMatchingMode
                    isOpen={isModalConfirmChangeMatchingModeOpen}
                    isLoading={isLoading}
                    handleClose={handleCloseModalConfirmChangeMatchingMode}
                    handleConfirm={handleConfirm}
                    seriesId={series.id}
                    matchingMode={selectedMode}
                />
            )}
        </Modal>
    );
}
